import { useEffect, useRef, useState } from 'react';
import { StringEnum } from '../../constants/enums/move-process-enum';
import NonConformityStatusEnum from '../../constants/enums/non-conformance-status-enum';
import { getTenantIdFromLocalStorage } from '../../helpers/localstorage-helper';
import { useStore } from '../../contexts/store-provider';
import {
  ToolBomUsageModeEnum,
  ToolBomUsageStateEnum,
} from '../../models/response/IToolBOMResponse';
import { ProcessTypeEnum } from '../../constants/enums';
import toast from 'react-hot-toast';
import { formatMessage } from '../../translations/format-message';
import {
  useLedSerial,
  buildStopLedMessage,
  clearLastLedMessage,
} from './hooks/use-led-serial';

export const useOperatorAssignSingle = (context: any) => {
  const { operatorStore, toolBOMStore } = useStore();
  const {
    enabled,
    taskId,
    processType,
    OperatorResponse,
    getOperatorState,
    resetOperatorResponse,
    resetCompleteProcessState,
    completeProcessState,
    removeForAbnormalityState,
    resetRemoveForAbnormalityState,
    GetOperatorService,
    CompleteProcessService,
    UpdateProcessService,
    startWebWorkerTimer,
    stopWebWorkerTimer,
    resetWebWorkerTimer,
    onSaveTeplarOrder,
    bulidCheckItemsPayload,
    buildMemoPayload,
    buildDimensionPayload,
    buildBOMCheckListPayload,
    mergedTabs,
    trayHistory,
    setTrayHistory,
    trayIndex,
    setTrayIndex,
    selectedTrayId,
    setSelectedTrayId,
    setActiveIndex,
    setSubmittedCheckItems,
    setMemo,
    setDimensionChecks,
    setCanvasDrawings,
    setBomDataListState,
    startDateTime,
    setStartDateTime,
    startTimer,
    setStartTimer,
    timer,
    setTimer,
    webWorkerTime,
    setWebWorkerTime,
    isTimerRunning,
    setIsTimerRunning,
    isProcessStarted,
    setIsProcessStarted,
    operatorCount,
    setOperatorCount,
    scanBuffer,
    setScanBuffer,
    lastKeyTime,
    setLastKeyTime,
    traySelectRef,
    removeTaskToggleHandler,
    setRemoveDuration,
    areAllTabsChecked,
    isBOMValidated,
    bomDataList,
    resetGetOperatorState,
    buildTeplarListPayload,
    // setIsRemoveForAbnormalityClicked,
    ledCutList,
    setLedCutListState,
    ledLengthList,
    setLedLengthListState,
    correctionRequestToggleHandler,
    setOpenNonConformity,
    SubmitNonConformanceWork,
    teplarList,
    setTeplarList,
    isRemoveForAbnormalityClicked,
    setIsRemoveForAbnormalityClicked,
    CheckProcessStatus,
    showStart,
    setShowStart,
    showConfirmModal,
    updateProcessState,
  } = context;

  const { sendCommand, isConnected } = useLedSerial();

  const [abnormalTrays, setAbnormalTrays] = useState<string[]>([]);
  const [pausedTrayIds, setPausedTrayIds] = useState<string[]>([]);
  // completeProcessState is shared between the Complete and Pause calls (both go through
  // CompleteProcessService) - this ref records which tool numbers to report to
  // UpdateToolStatusByToolManagementNo once completeProcessState.success fires, and stays null
  // for Pause so that call never fires for anything but a real Complete.
  const pendingCompleteToolNumbersRef = useRef<string[] | null>(null);
  // Same idea, but for the Start call (updateProcessState/UpdateProcessService).
  const pendingStartToolNumbersRef = useRef<string[] | null>(null);
  // Multi-tray "Keep Tool" flow: true while the next tray's transition is held behind the
  // operator resolving the popup (mandatory for now).
  const [showKeepToolPopup, setShowKeepToolPopup] = useState(false);
  // Tool numbers the operator chose to keep from the previous tray, reported for the new tray
  // once its trayId is known (after handleRemoveTray moves selection forward).
  const pendingKeepToolsRef = useRef<string[] | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (pendingKeepToolsRef.current && selectedTrayId) {
      // FactoryId/ToolModel are pinned to what snapshotForKeep captured, since the Tool BOM
      // tab's left table (and the live values it owns) may already have unmounted/cleared by
      // the time this fires, after the tray transition.
      toolBOMStore.ReportIncrementalToolStart(
        selectedTrayId,
        Number(taskId),
        pendingKeepToolsRef.current,
        toolBOMStore.keepFactoryId,
        toolBOMStore.keepToolModel
      );
      pendingKeepToolsRef.current = null;
    }
  }, [enabled, selectedTrayId]);

  const saveTeplarOrderIfNeeded = async () => {
    if (Number(processType) !== 2 || !onSaveTeplarOrder) {
      return;
    }
    await onSaveTeplarOrder();
  };

  useEffect(() => {
    if (!enabled) return;
    if (completeProcessState.success) {
      resetCompleteProcessState();
      if (pendingCompleteToolNumbersRef.current) {
        toolBOMStore.UpdateToolStatusByToolManagementNoService(
          pendingCompleteToolNumbersRef.current,
          false
        );
        pendingCompleteToolNumbersRef.current = null;
      }
    }
  }, [completeProcessState.success, enabled]);

  useEffect(() => {
    if (!enabled) return;
    if (completeProcessState.error) {
      // A failed Complete must not leak into a later Pause's success and wrongly trigger
      // UpdateToolStatusByToolManagementNo for it.
      pendingCompleteToolNumbersRef.current = null;
    }
  }, [completeProcessState.error, enabled]);

  useEffect(() => {
    if (!enabled) return;
    if (updateProcessState.success) {
      if (pendingStartToolNumbersRef.current) {
        toolBOMStore.UpdateToolStatusByToolManagementNoService(
          pendingStartToolNumbersRef.current,
          true
        );
        pendingStartToolNumbersRef.current = null;
      }
    }
  }, [updateProcessState.success, enabled]);

  useEffect(() => {
    if (!enabled) return;
    if (updateProcessState.error) {
      pendingStartToolNumbersRef.current = null;
    }
  }, [updateProcessState.error, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const handleKeyDown = (e) => {
      if (toolBOMStore.isToolScanPopupOpen) return;

      const currentTime = Date.now();
      const timeDiff = currentTime - lastKeyTime;

      if (!e.key) return;

      if (
        e.key === 'Shift' ||
        e.key === 'Control' ||
        e.key === 'Alt' ||
        e.key === 'CapsLock' ||
        (e.key.length > 1 && e.key !== 'Enter')
      ) {
        return;
      }

      if (e.key === 'Enter') {
        if (timeDiff > 100) {
          e.preventDefault();
          return;
        }
        // if (isTimerRunning) {
        //   e.preventDefault();
        //   toast.error(formatMessage('please_complete_the_tray_to_scan'));
        //   return;
        // }
        e.preventDefault();
        const cleanedScan = scanBuffer.trim();
        if (cleanedScan === selectedTrayId) {
          return;
        }
        if (trayHistory.includes(cleanedScan)) {
          // Explicitly select scanned tray when already in history
          setSelectedTrayId(cleanedScan);
          getSelectedTrayDetails(cleanedScan);
          traySelectRef.current?.focus();
          return;
        } else if (isTimerRunning) {
          handlePauseTimer();
          setTrayHistory((prev) => [...prev, cleanedScan]);
          // Select scanned tray when pausing timer
          setSelectedTrayId(cleanedScan);
          getActiveTrayListServiceHandler(cleanedScan);
          setScanBuffer('');
          traySelectRef.current?.focus();
          return;
        }
        setTrayHistory((prev) => [...prev, cleanedScan]);
        // Explicitly select newly scanned tray so dropdown updates immediately
        setSelectedTrayId(cleanedScan);
        getActiveTrayListServiceHandler(cleanedScan);
        setScanBuffer('');
        traySelectRef.current?.focus();
        return;
      }

      if (timeDiff > 100) {
        setScanBuffer(e.key);
      } else {
        setScanBuffer((prev) => prev + e.key);
      }

      setLastKeyTime(currentTime);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, scanBuffer, lastKeyTime, trayHistory, isTimerRunning]);

  useEffect(() => {
    if (!enabled) return;
    if (removeForAbnormalityState.success) {
      removeTaskToggleHandler();
      resetRemoveForAbnormalityState();
      setIsTimerRunning(false);
      setOperatorCount(1);
      setIsProcessStarted(false);
      setTimer(0);
      resetWebWorkerTimer();
      syncSelectionAfterTrayRemoval();
    }
  }, [enabled, removeForAbnormalityState.success]);

  useEffect(() => {
    if (!enabled) return;
    if (removeForAbnormalityState.error) {
      resetRemoveForAbnormalityState();
    }
  }, [enabled, removeForAbnormalityState.error]);

  useEffect(() => {
    if (!enabled) return;
    const hasCurrentTrayResponse =
      OperatorResponse?.TrayId &&
      selectedTrayId &&
      String(OperatorResponse.TrayId) === String(selectedTrayId);

    if (!hasCurrentTrayResponse) {
      if (trayHistory.length === 0) {
        setTimer(0);
      }
      return;
    }

    if (OperatorResponse.TimeDuration) {
      const initialDuration = Number(OperatorResponse.TimeDuration) || 0;
      setTimer(initialDuration);
      setWebWorkerTime(initialDuration);
    }

    if (OperatorResponse.CurrentState === StringEnum.Lock) {
      setAbnormalTrays((prev) =>
        prev.includes(OperatorResponse.TrayId)
          ? prev
          : [...prev, OperatorResponse.TrayId]
      );
    }

    if (OperatorResponse.FileList && OperatorResponse.FileList.length > 0) {
      const isTrue = (val: any) =>
        val === true || String(val).toLowerCase() === 'true';

      let bestIndex = 0;
      let bestPriority = 2;

      OperatorResponse.FileList.forEach((file, index) => {
        let priority = 2;
        if (isTrue(file.IsDefault) || isTrue(file.isDefault)) {
          priority = 0;
        } else if (isTrue(file.IsDimension) || isTrue(file.isDimension)) {
          priority = 1;
        }

        if (priority < bestPriority) {
          bestPriority = priority;
          bestIndex = index;
        }
      });

      setActiveIndex(bestIndex);
    }
  }, [enabled, OperatorResponse, selectedTrayId, trayHistory.length]);

  useEffect(() => {
    if (!enabled) return;
    if (!OperatorResponse?.FileList?.length) return;

    const isTrue = (val: any) =>
      val === true || String(val).toLowerCase() === 'true';
    const defaultIndex = mergedTabs.findIndex(
      (t) => isTrue(t.data?.IsDefault) || isTrue(t.data?.isDefault)
    );
    if (defaultIndex !== -1) {
      setActiveIndex(defaultIndex);
    } else {
      const dimensionIndex = mergedTabs.findIndex(
        (t) => isTrue(t.data?.IsDimension) || isTrue(t.data?.isDimension)
      );
      if (dimensionIndex !== -1) {
        setActiveIndex(dimensionIndex);
      } else {
        setActiveIndex(0);
      }
    }

    const hydratedCheckItems: any = {};
    const hydratedMemo: Record<number, string> = {};

    OperatorResponse.FileList?.forEach((fileItem: any, fileIndex: number) => {
      const checkItemsGroups = fileItem?.CheckItems ?? [];
      const memoKey = fileItem?.TabIndex ?? fileIndex;
      const existingMemo =
        Array.isArray(fileItem?.MemoMessage) && fileItem.MemoMessage.length > 0
          ? fileItem.MemoMessage[0]
          : null;

      hydratedMemo[memoKey] = existingMemo?.Memo ?? '';

      if (checkItemsGroups.length === 0) return;

      hydratedCheckItems[fileIndex] = {};

      checkItemsGroups.forEach((group: any) => {
        const messages = group?.CheckItemMessage ?? [];
        messages.forEach((item: any) => {
          if (item.Id === undefined || item.Id === null) return;
          hydratedCheckItems[fileIndex][item.Id] = {
            isChecked:
              item.IsChecked === true ||
              String(item.IsChecked).toLowerCase() === 'true',
            InputMessage: item.InputMessage || '',
          };
        });
      });
    });

    setSubmittedCheckItems(hydratedCheckItems);
    setMemo(hydratedMemo);
    setCanvasDrawings({});
  }, [enabled, OperatorResponse, mergedTabs]);

  useEffect(() => {
    if (!enabled) return;
    if (mergedTabs.length === 0) return;

    const initialState: any = {};

    mergedTabs.forEach((tab, tabIndex) => {
      const checkItems = tab?.data?.CheckItems ?? [];

      checkItems.forEach((item: any) => {
        item.CheckItemMessage?.forEach((msg: any) => {
          if (!initialState[tabIndex]) initialState[tabIndex] = {};
          initialState[tabIndex][msg.Id] = {
            isChecked: msg.IsChecked,
            InputMessage: msg.InputMessage || '',
          };
        });
      });
    });

    // Keep what the operator already entered (e.g. across a language switch,
    // which rebuilds mergedTabs); only fill in entries that don't exist yet.
    setSubmittedCheckItems((prev) => {
      const next: any = { ...prev };
      Object.keys(initialState).forEach((tabKey) => {
        next[tabKey] = { ...initialState[tabKey], ...(prev?.[tabKey] || {}) };
      });
      return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
    });
  }, [enabled, mergedTabs]);

  useEffect(() => {
    if (!enabled) return;
    if (!OperatorResponse?.FileList?.length) return;

    const hydratedDimensions: Record<string, boolean> = {};

    OperatorResponse.FileList.forEach((file: any) => {
      const dims = file.DimensionsData ?? [];

      dims.forEach((dim: any) => {
        if (dim.DimensionType !== 1) return;

        const values = dim.DimensionValues ?? [];

        values.forEach((val: string, rowIndex: number) => {
          const [, checkedFlag] = String(val).split('$');
          const key = `${rowIndex}-${dim.Id}`;
          hydratedDimensions[key] = checkedFlag === 'true';
        });
      });
    });

    setDimensionChecks(hydratedDimensions);
  }, [enabled, OperatorResponse]);

  useEffect(() => {
    if (!enabled) return;
    if (bomDataList?.length > 0) {
      setBomDataListState(bomDataList);
    } else {
      setBomDataListState([]);
    }
  }, [enabled, bomDataList]);

  useEffect(() => {
    if (!enabled) return;
    if (ledCutList?.length > 0) {
      setLedCutListState(ledCutList);
    } else {
      setLedCutListState([]);
    }
  }, [enabled, ledCutList]);

  useEffect(() => {
    if (!enabled) return;
    if (ledLengthList?.length > 0) {
      setLedLengthListState(ledLengthList);
    } else {
      setLedLengthListState([]);
    }
  }, [enabled, ledLengthList]);

  useEffect(() => {
    if (!enabled) return;
    const shouldStart = trayHistory.length > 0 && getOperatorState.success;
    // Every time a tray is opened, fetch whatever tools are already recorded against it (merged
    // with any Keep Tool tools already carried in) BEFORE starting it, so the Tool Scan popup
    // shows them and the Start only sends what's genuinely new.
    const trayIdValue = String(selectedTrayId || '').trim();
    if (
      getOperatorState.success &&
      trayIdValue &&
      String(OperatorResponse?.TrayId) === trayIdValue
    ) {
      Promise.resolve(
        toolBOMStore.GetToolMgtNumberByTrayAndProceesIdService(
          trayIdValue,
          Number(taskId),
          ToolBomUsageModeEnum.Single,
          Number(getTenantIdFromLocalStorage())
        )
      ).finally(() => {
        if (shouldStart) handleStartClick();
      });
    } else if (shouldStart) {
      // const initialDuration = Number(OperatorResponse?.TimeDuration) || 0;
      handleStartClick();
    }
    resetGetOperatorState();
  }, [enabled, getOperatorState.success]);

  // A scanned tray that doesn't exist (or any other GetOperatorService failure) must not leave
  // the operator stuck on a dead tray with no tools visible - toolBOMStore.switchTrayTools was
  // already called for the failed tray before this fetch even started (see
  // getActiveTrayListServiceHandler/getSelectedTrayDetails), so without reverting here the
  // previously-scanned tools stay stashed under the old tray id while the screen shows the new,
  // empty, broken one. Same recovery pattern as Batch mode.
  useEffect(() => {
    if (!enabled) return;
    if (operatorStore.getOperatorState.error) {
      operatorStore.resetGetOperatorState();
      const updatedTrayHistory = trayHistory.filter(
        (trayId: string) => trayId !== selectedTrayId
      );
      setTrayHistory(updatedTrayHistory);
      const previousTrayId = updatedTrayHistory[updatedTrayHistory.length - 1];
      if (previousTrayId) {
        setSelectedTrayId(previousTrayId);
        getSelectedTrayDetails(previousTrayId);
      } else {
        setSelectedTrayId(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, operatorStore.getOperatorState.error]);

  // Synchronize selectedTrayId whenever trayHistory updates to ensure the latest scanned tray
  // is selected in the dropdown and its details are loaded if no valid tray is currently active.
  useEffect(() => {
    if (!enabled) return;
    if (
      trayHistory.length > 0 &&
      (!selectedTrayId || !trayHistory.includes(selectedTrayId))
    ) {
      const nextTray = trayHistory[trayHistory.length - 1];
      setSelectedTrayId(nextTray);
      if (
        !OperatorResponse?.TrayId ||
        String(OperatorResponse.TrayId) !== String(nextTray)
      ) {
        getSelectedTrayDetails(nextTray);
      }
    }
  }, [enabled, trayHistory, selectedTrayId, OperatorResponse?.TrayId]);

  // Values sent for a previous tray must not be re-sent when stopping on another tray.
  useEffect(() => {
    clearLastLedMessage();
  }, [selectedTrayId]);

  const stopLEDGlow = async () => {
    if (
      processType !== ProcessTypeEnum.LedCut &&
      processType !== ProcessTypeEnum.LedLength
    ) {
      return;
    }
    // Port already closed: nothing to stop, and do not reconnect.
    if (!isConnected()) return;
    try {
      // Time 0 with the previously sent LED values; zeroed values if none were sent.
      let message = buildStopLedMessage();
      if (!message) {
        message =
          processType === ProcessTypeEnum.LedLength
            ? `1000,0,G,0,R,0,G,0\r\n`
            : `1000,0,R,0\r\n`;
      }

      // Final command: sends stop if the port is open, closes it, never reconnects.
      const response = await sendCommand(message, { final: true });
      console.log('LED Cut device response:', response);

      toast.success(formatMessage('led_command_success'));
    } catch (error: any) {
      console.error('Serial error:', error);
      toast.error(error?.message || formatMessage('led_error_unknown'));
    }
  };

  const removeTrayDataOnProcessCompletion = (trayId: string) => {
    const trayData = JSON.parse(localStorage.getItem('trayData') || '{}');
    if (trayData[trayId]) {
      delete trayData[trayId];
      localStorage.setItem('trayData', JSON.stringify(trayData));
    }
    if (trayData.length === 1 && trayData[trayId]) {
      localStorage.removeItem('trayData');
    }
  };

  const getActiveTrayListServiceHandler = (trayId?: string) => {
    const currentTray = trayId || trayHistory[trayIndex];
    if (!currentTray) return;

    // Tools belong to the tray they were scanned for - stash the tray being left and load
    // (or start empty for) the one being opened.
    toolBOMStore.switchTrayTools(String(currentTray).trim());

    // Don't clear existing data immediately
    setSelectedTrayId(currentTray);
    // setActiveIndex(-1);
    // resetOperatorResponse();
    // setSubmittedCheckItems({});
    // setMemo({});
    // setDimensionChecks({});
    // setCanvasDrawings({});

    if (trayId) {
      setTrayIndex(0);
    } else if (trayHistory.length > 0) {
      setTrayIndex((prevIndex) => (prevIndex + 1) % trayHistory.length);
    }

    GetOperatorService(
      Number(getTenantIdFromLocalStorage()),
      currentTray,
      taskId,
      Number(processType)
    );
  };

  const getSelectedTrayDetails = (trayId: string) => {
    toolBOMStore.switchTrayTools(String(trayId).trim());
    setSelectedTrayId(trayId);
    // setSelectedTray(trayId);
    setActiveIndex(-1);
    resetOperatorResponse();
    setSubmittedCheckItems({});
    setMemo({});
    setDimensionChecks({});
    setCanvasDrawings({});
    GetOperatorService(
      Number(getTenantIdFromLocalStorage()),
      trayId,
      taskId,
      Number(processType)
    );
    setWebWorkerTime(OperatorResponse.TimeDuration || 0);
    setOperatorCount(1);
    setIsTimerRunning(false);
  };

  const completeTrayLocalCleanup = (removedId?: string) => {
    const removedTrayId = removedId ?? String(selectedTrayId || '').trim();
    const updatedTrayHistory = trayHistory.filter(
      (trayId: string) => trayId !== selectedTrayId
    );

    setTrayHistory(updatedTrayHistory);
    setAbnormalTrays((prev) =>
      prev.filter((trayId: string) => trayId !== selectedTrayId)
    );
    setPausedTrayIds((prev) =>
      prev.filter((trayId: string) => trayId !== selectedTrayId)
    );
    toolBOMStore.removeTrayTools(removedTrayId);

    if (updatedTrayHistory.length === 0) {
      setSelectedTrayId(null);
      // setSelectedTray('');
      setActiveIndex(-1);
      setSubmittedCheckItems({});
      setMemo({});
      setDimensionChecks({});
      setCanvasDrawings({});
      setBomDataListState([]);
      setLedCutListState([]);
      setLedLengthListState([]);
      resetOperatorResponse();
      toolBOMStore.resetSessionToolTracking();
      return;
    }

    setSelectedTrayId(updatedTrayHistory[updatedTrayHistory.length - 1]);
    getSelectedTrayDetails(updatedTrayHistory[updatedTrayHistory.length - 1]);
  };

  const syncSelectionAfterTrayRemoval = () => {
    const removedTrayId = String(selectedTrayId || '').trim();
    const removedTrayTools =
      toolBOMStore.trayToolsMap[removedTrayId]?.scanned ?? toolBOMStore.scannedToolNumbers;

    toolBOMStore
      .ReleaseToolService(removedTrayTools, [removedTrayId], Number(taskId), ToolBomUsageModeEnum.Single)
      .then(() => {
        if (!toolBOMStore.releaseToolState.error) {
          completeTrayLocalCleanup(removedTrayId);
          setTimer(0);
          resetWebWorkerTimer();
        }
      });
  };

  const handleRemoveTray = () => {
    syncSelectionAfterTrayRemoval();
  };

  const completeSuccess = () => {
    const trayIdValue = selectedTrayId;

    setIsTimerRunning(false);
    setTimer(0);
    resetWebWorkerTimer();
    setOperatorCount(1);
    setIsProcessStarted(false);
    setPausedTrayIds((prev) =>
      prev.filter((trayId: string) => trayId !== trayIdValue)
    );

    const remainingTrays = trayHistory.filter(
      (trayId: string) => trayId !== trayIdValue
    );
    if (remainingTrays.length > 0 && toolBOMStore.scannedToolNumbers.length > 0) {
      toolBOMStore.snapshotForKeep();
      setShowKeepToolPopup(true);
    } else if (remainingTrays.length > 0) {
      toolBOMStore.snapshotForKeep();
      handleKeepToolSave([]);
    } else {
      completeTrayLocalCleanup(String(trayIdValue || '').trim());
      // Last tray done - nothing left to carry forward.
      // toolBOMStore.resetSessionToolTracking();
    }

    setMemo([]);
    setDimensionChecks([]);
    setSubmittedCheckItems({});
    setCanvasDrawings({});
    removeTrayDataOnProcessCompletion(trayIdValue);
    resetWebWorkerTimer();
  };

  /**
   * This function is used after a successful Non-Conformity report for the current tray - same
   * Keep Tool tray-transition gate as a real Complete (snapshotForKeep + showKeepToolPopup) when
   * other trays are still queued, otherwise the tray is just removed.
   */
  const handleNonConformitySuccess = () => {
    toolBOMStore.resetToolBomToolManagementList();
    setIsTimerRunning(false);
    setTimer(0);
    resetWebWorkerTimer();
    setOperatorCount(1);
    setIsProcessStarted(false);
    const remainingTrays = trayHistory.filter(
      (trayId: string) => trayId !== selectedTrayId
    );
    if (remainingTrays.length > 0 && toolBOMStore.scannedToolNumbers.length > 0) {
      toolBOMStore.snapshotForKeep();
      setShowKeepToolPopup(true);
    } else if (remainingTrays.length > 0) {
      toolBOMStore.snapshotForKeep();
      handleKeepToolSave([]);
    } else {
      completeTrayLocalCleanup(String(selectedTrayId || '').trim());
    }
  };

  /**
   * This function is used to carry the operator's chosen tools into the next tray once the
   * Keep Tool popup's Save is clicked - the new tray's transition (deferred by completeSuccess)
   * proceeds only after this.
   */
  const handleKeepToolSave = (keptToolNumbers: string[]) => {
    // Same tray syncSelectionAfterTrayRemoval will pick next. The finished tray's tools are
    // dropped and the next tray's own tools loaded first (it may be a paused tray that already
    // has some), then the kept tools are added on top - only the ones it doesn't already have
    // get reported.
    const finishedTrayId = String(selectedTrayId || '').trim();
    const notKeptTools = toolBOMStore.lastTrayScannedToolNumbers.filter(
      (tool: string) => !keptToolNumbers.includes(tool)
    );
    if (notKeptTools.length > 0) {
      toolBOMStore.UpdateToolStatusByToolManagementNoService(
        notKeptTools,
        false
      );
    }
    const nextTrayId = trayHistory
      .filter((trayId: string) => trayId !== selectedTrayId)
      .slice(-1)[0];
    toolBOMStore.removeTrayTools(finishedTrayId);
    if (nextTrayId) toolBOMStore.switchTrayTools(String(nextTrayId).trim());
    const toReport = toolBOMStore.resetForNextTray(keptToolNumbers);
    pendingKeepToolsRef.current = toReport.length > 0 ? toReport : null;
    setShowKeepToolPopup(false);
    completeTrayLocalCleanup(finishedTrayId);
  };

  const handleKeepToolClose = () => {
    setShowKeepToolPopup(false);
  };

  const handleCompleteProcess = async () => {
    saveTeplarOrderIfNeeded();
    stopLEDGlow();
    const trayIdValue = String(selectedTrayId || '').trim();
    // Only Complete (not Pause) should mark the used tools Inspected/InUse once the Operator
    // request succeeds - and only once the LAST tray in a multi-tray single-mode queue
    // completes, covering every tool scanned/kept across the whole run (matches batch mode's
    // Complete behavior). Earlier trays in the queue skip this - their tools already went out
    // via ReportIncrementalToolStart on each Tool Scan/Keep Tool Save.
    const isLastTray =
      trayHistory.filter((id: string) => id !== trayIdValue).length === 0;
    pendingCompleteToolNumbersRef.current = isLastTray
      ? [...toolBOMStore.allSessionScannedToolNumbers]
      : null;

    const submitObj = {
      TenantId: Number(getTenantIdFromLocalStorage()),
      TrayId: [trayIdValue],
      Count: 1,
      ProcessId: taskId,
      StartTime: startDateTime,
      EndTime: new Date().toISOString(),
      Duration: webWorkerTime - startTimer,
      NoOfOperators: operatorCount,
      CurrentState: abnormalTrays.includes(selectedTrayId)
        ? StringEnum.Lock
        : '',
      IsPaused: false,
      IsBatch: false,
      CheckItemList: bulidCheckItemsPayload(),
      MemoMessage: buildMemoPayload(),
      Dimensions: buildDimensionPayload(),
      BOMCheckList: buildBOMCheckListPayload(),
      TeplarList: buildTeplarListPayload(),
      // Complete now reports Tool BOM usage inline in this same Operator request instead of a
      // separate ToolManagement/ToolBomUsageInfo call (Start still makes that call on its own).
      ToolBomUsageInfoDetailss: toolBOMStore.BuildToolBomUsageDetails(
        [trayIdValue],
        Number(taskId),
        webWorkerTime - startTimer,
        ToolBomUsageStateEnum.Complete,
        ToolBomUsageModeEnum.Single
      ),
      IsFollowUp: processType === ProcessTypeEnum.FollowUpPicking,
    };
    const completed = await CompleteProcessService(submitObj);
    if (!completed) return;
    completeSuccess();
  };

  const handleStartClick = async () => {
    if (processType !== ProcessTypeEnum.FollowUpPicking) {
      const updateObj = {
        TenantId: Number(getTenantIdFromLocalStorage()),
        TrayId: [String(selectedTrayId || '').trim()],
        ProcessId: taskId,
        SetActive: true,
      };
      await CheckProcessStatus(updateObj);
    } else {
      handleStartTimer();
    }
  };

  const handleStartTimer = async (initialDuration?: number) => {
    const startTrayId = String(selectedTrayId || '').trim();
    // Only tools not already saved for this tray - a resumed (paused) tray's tools are already in
    // the DB (fetched before this Start), so resending them would insert duplicates.
    const unsentTools = toolBOMStore.scannedToolNumbers.filter(
      (tool: string) => !toolBOMStore.sentScannedToolNumbers.includes(tool)
    );
    pendingStartToolNumbersRef.current = [...unsentTools];
    const updateObj = {
      TenantId: Number(getTenantIdFromLocalStorage()),
      TrayId: [startTrayId],
      ProcessId: taskId,
      SetActive: true,
      // Same shape sent on Pause/Complete, but Duration is always 0 here since the process is
      // just starting.
      ToolBomUsageInfoDetailss: toolBOMStore.BuildToolBomUsageDetails(
        [startTrayId],
        Number(taskId),
        0,
        ToolBomUsageStateEnum.Start,
        ToolBomUsageModeEnum.Single,
        unsentTools
      ),
    };
    if (processType !== ProcessTypeEnum.FollowUpPicking) {
      const started = await UpdateProcessService(updateObj);
      if (!started) return;
    }
    toolBOMStore.markToolsSent(unsentTools);
    const resolvedInitialDuration =
      typeof initialDuration === 'number' ? initialDuration : webWorkerTime;

    if (selectedTrayId) {
      setPausedTrayIds((prev) =>
        prev.filter((trayId: string) => trayId !== selectedTrayId)
      );
    }
    setTimer(resolvedInitialDuration);
    setWebWorkerTime(resolvedInitialDuration);
    startWebWorkerTimer(resolvedInitialDuration);
    setStartDateTime(new Date().toISOString());
    setIsTimerRunning(true);
    setIsProcessStarted(true);
    setStartTimer(resolvedInitialDuration);
  };

  const PausedSuccess = () => {
    setIsTimerRunning(false);
    if (selectedTrayId) {
      setPausedTrayIds((prev) =>
        prev.includes(selectedTrayId) ? prev : [...prev, selectedTrayId]
      );
    }
  };

  const handlePauseTimer = () => {
    stopWebWorkerTimer();
    saveTeplarOrderIfNeeded();
    stopLEDGlow();
    const trayIdValue = String(selectedTrayId || '').trim();
    const submitObj = {
      TenantId: Number(getTenantIdFromLocalStorage()),
      TrayId: [trayIdValue],
      Count: 1,
      ProcessId: taskId,
      StartTime: startDateTime,
      EndTime: new Date().toISOString(),
      Duration: webWorkerTime - startTimer,
      NoOfOperators: operatorCount,
      CurrentState: abnormalTrays.includes(selectedTrayId)
        ? StringEnum.Lock
        : '',
      IsPaused: isTimerRunning ? true : false,
      IsBatch: false,
      CheckItemList: bulidCheckItemsPayload(),
      MemoMessage: buildMemoPayload(),
      Dimensions: buildDimensionPayload(),
      BOMCheckList: buildBOMCheckListPayload(),
      TeplarList: buildTeplarListPayload(),
      // Pause now reports Tool BOM usage inline in this same Operator request instead of a
      // separate ToolManagement/ToolBomUsageInfo call (Start still makes that call on its own).
      ToolBomUsageInfoDetailss: toolBOMStore.BuildToolBomUsageDetails(
        [trayIdValue],
        Number(taskId),
        webWorkerTime - startTimer,
        ToolBomUsageStateEnum.Pause,
        ToolBomUsageModeEnum.Single
      ),
      IsFollowUp: processType === ProcessTypeEnum.FollowUpPicking,
    };

    const pausePromise = CompleteProcessService(submitObj);
    PausedSuccess();
    return pausePromise;
  };

  // Pause button: on a non-conformance process (NCId/NGId > 0), also submit the NC work done so far
  const handlePauseClick = () => {
    if (OperatorResponse?.NCId > 0 || OperatorResponse?.NGId > 0) {
      SubmitNonConformanceWork({
        TenantId: Number(getTenantIdFromLocalStorage()),
        NCId: OperatorResponse?.NCId,
        NGId: OperatorResponse?.NGId,
        StartTime: startDateTime,
        EndTime: new Date().toISOString(),
        duration: webWorkerTime - startTimer,
        ProcessId: Number(taskId),
        WorkComment: '',
        IsPause: true
      });
    }
    return handlePauseTimer();
  };

  const removeTrayServiceHandler = () => {
    const obj = {
      TenantId: Number(getTenantIdFromLocalStorage()),
      TrayId: [selectedTrayId],
      ProcessId: taskId,
      SetActive: false,
    };
    // UpdateProcessService(obj);
    handleRemoveTray();
  };

  const handleRemoveForAbnormality = () => {
    setIsRemoveForAbnormalityClicked(isTimerRunning);
    if (isTimerRunning) {
      stopWebWorkerTimer();
      setIsTimerRunning(false);
    }
    setRemoveDuration(isTimerRunning ? webWorkerTime - startTimer : 0);
    saveTeplarOrderIfNeeded();
    removeTaskToggleHandler();
  };

  const handleCorrectionRequest = () => {
    correctionRequestToggleHandler();
  };

  const nonConformityHandler = () => {
    setOpenNonConformity(true);
    if (isTimerRunning) {
      handlePauseTimer();
    }
  };

  const handleTraySelectChange = (nextTrayId: string) => {
    if (isTimerRunning) {
      setIsTimerRunning(false);
      handlePauseTimer();
      // CompleteProcessService(submitObj);
      setIsProcessStarted(false);
    }
    getSelectedTrayDetails(nextTrayId);
  };

  const addTray = (trayId: string) => {
    if (isTimerRunning) {
      handlePauseTimer();
      if (trayHistory.includes(trayId)) {
        getSelectedTrayDetails(trayId);
        return;
      } else {
        setTrayHistory((prev) => [...prev, trayId]);
        getSelectedTrayDetails(trayId);
      }
    } else {
      setTrayHistory((prev) => [...prev, trayId]);
      getSelectedTrayDetails(trayId);
    }
  };

  const shouldShowTrayWarning = (tray: string) => {
    if (!isTimerRunning) return false;
    return tray === selectedTrayId && !(areAllTabsChecked && isBOMValidated);
  };

  const shouldShowCheckMark = (tray: string) => {
    if (!isTimerRunning) return false;
    return tray === selectedTrayId && areAllTabsChecked && isBOMValidated;
  };

  const canShowStartButton =
    (!isTimerRunning &&
      trayHistory.length > 0 &&
      selectedTrayId &&
      pausedTrayIds.includes(selectedTrayId)) ||
    !showConfirmModal;

  const handleReportCorrectionClick = () => {
    setOpenNonConformity(true);
    if (isTimerRunning) {
      handlePauseTimer();
    }
  };

  const buttons = [
    ...(isTimerRunning
      ? [
          {
            labelKey: 'pause',
            className: 'btn-secondary',
            onClick: handlePauseClick,
            isDisabled: !isTimerRunning,
          },
          {
            labelKey:
              OperatorResponse?.NCStatus ===
                NonConformityStatusEnum.NCApproved ||
              OperatorResponse?.NCStatus === NonConformityStatusEnum.NG
                ? 'report_correction'
                : 'complete',
            className: 'btn-primary',
            onClick:
              OperatorResponse?.NCStatus ===
                NonConformityStatusEnum.NCApproved ||
              OperatorResponse?.NCStatus === NonConformityStatusEnum.NG
                ? handleReportCorrectionClick
                : handleCompleteProcess,
            isDisabled:
              trayHistory.length === 0 ||
              !isProcessStarted ||
              !(areAllTabsChecked && isBOMValidated),
          },
        ]
      : canShowStartButton
        ? [
            {
              labelKey: 'start',
              className: 'btn-success',
              onClick: handleStartClick,
              isDisabled: trayHistory.length === 0 || isTimerRunning,
            },
          ]
        : []),
    {
      labelKey: 'remove',
      className: 'btn-danger',
      onClick: removeTrayServiceHandler,
      isDisabled: trayHistory.length === 0 || isTimerRunning,
    },
    ...(OperatorResponse?.NCStatus === -1
      ? [
          // {
          //   labelKey: 'remove_for_abnormality',
          //   className: 'btn-outline-danger',
          //   onClick: handleRemoveForAbnormality,
          //   isDisabled: trayHistory.length === 0,
          // },
          {
            labelKey: 'correction_request',
            className: 'btn-outline-danger',
            onClick: handleCorrectionRequest,
            isDisabled: trayHistory.length === 0,
          },
          {
            labelKey: 'non_conformity',
            className: 'btn-outline-danger',
            onClick: nonConformityHandler,
            isDisabled: trayHistory.length === 0,
          },
        ]
      : []),
  ];

  return {
    buttons,
    shouldShowTrayWarning,
    handleTraySelectChange,
    addTray,
    handlePauseTimer,
    shouldShowCheckMark,
    handleStartTimer,
    handleRemoveTray,
    handleStartClick,
    completeSuccess,
    PausedSuccess,
    showKeepToolPopup,
    handleKeepToolSave,
    handleKeepToolClose,
    handleCompleteProcess,
    handleNonConformitySuccess,
  };
};
